import { useEffect, useRef, useCallback } from 'react';
import { realtimeManager } from '@/lib/realtimeManager';
import { useToast } from '@/components/common/Toast';
import { debounce } from 'lodash';

interface StockUpdatePayload {
  new: {
    id: string;
    product_id: string;
    date: string;
    initial_stock: number;
    current_stock: number;
    sold_stock: number;
    version: number;
  };
  old: {
    current_stock: number;
    sold_stock: number;
    version: number;
  };
}

interface ProductInfo {
  id: string;
  name: string;
  is_available: boolean;
}

interface UseRealtimeStockOptions {
  onStockUpdate?: (productId: string, currentStock: number, isAvailable: boolean) => void;
  onOutOfStock?: (productId: string, productName: string) => void;
  onBackInStock?: (productId: string, productName: string) => void;
  showToast?: boolean;
  debounceMs?: number;
  productInfoMap?: Map<string, ProductInfo>; // 상품 정보 매핑
}

/**
 * 실시간 재고 업데이트를 구독하는 훅
 */
export function useRealtimeStock(
  productIds: string[] | null,
  options: UseRealtimeStockOptions = {}
) {
  const {
    onStockUpdate,
    onOutOfStock,
    onBackInStock,
    showToast: shouldShowToast = true,
    debounceMs: _ = 300
  } = options;

  const { showToast } = useToast();
  const subscriberIdRef = useRef(`stock-${Date.now()}-${Math.random()}`);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // 디바운스된 토스트 함수
  const debouncedToast = useRef(
    debounce((message: string, duration: number = 2000) => {
      if (shouldShowToast) {
        showToast(message, duration);
      }
    }, 500)
  ).current;

  const handleStockUpdate = useCallback((payload: StockUpdatePayload) => {
    const { new: newData, old: oldData } = payload;
    const { product_id, current_stock } = newData;
    
    // 상품 정보 조회
    const productInfo = options.productInfoMap?.get(product_id);
    const productName = productInfo?.name || '상품';
    const isAvailable = current_stock > 0 && (productInfo?.is_available ?? true);

    // 재고 업데이트 콜백 호출
    onStockUpdate?.(product_id, current_stock, isAvailable);

    // 품절 상태 변경 감지
    const wasAvailable = oldData.current_stock > 0;
    const isNowAvailable = current_stock > 0;

    if (wasAvailable && !isNowAvailable) {
      // 품절됨
      onOutOfStock?.(product_id, productName);
      debouncedToast(`${productName}이(가) 품절되었습니다`);
    } else if (!wasAvailable && isNowAvailable) {
      // 재입고됨
      onBackInStock?.(product_id, productName);
      debouncedToast(`${productName}이(가) 재입고되었습니다`);
    } else if (current_stock !== oldData.current_stock) {
      // 재고 수량만 변경됨
      if (current_stock < 5 && current_stock > 0) {
        debouncedToast(`${productName}의 재고가 ${current_stock}개만 남았습니다`);
      }
    }
  }, [onStockUpdate, onOutOfStock, onBackInStock, debouncedToast, options.productInfoMap]);

  useEffect(() => {
    if (!productIds || productIds.length === 0) return;

    // 구독 시작
    unsubscribeRef.current = realtimeManager.subscribeToProductStock(
      productIds,
      handleStockUpdate,
      subscriberIdRef.current
    );

    // 클린업
    return () => {
      unsubscribeRef.current?.();
      debouncedToast.cancel();
    };
  }, [productIds?.join(','), handleStockUpdate]);

  return {
    subscriberId: subscriberIdRef.current
  };
}

/**
 * 매장의 모든 상품 재고 업데이트를 구독하는 훅
 */
export function useRealtimeRestaurantStock(
  restaurantId: string | null,
  options: UseRealtimeStockOptions = {}
) {
  const {
    onStockUpdate,
    onOutOfStock,
    onBackInStock,
    showToast: shouldShowToast = true,
    debounceMs: __ = 300
  } = options;

  const { showToast } = useToast();
  const subscriberIdRef = useRef(`restaurant-stock-${Date.now()}-${Math.random()}`);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const debouncedToast = useRef(
    debounce((message: string, duration: number = 2000) => {
      if (shouldShowToast) {
        showToast(message, duration);
      }
    }, 500)
  ).current;

  const handleStockUpdate = useCallback((payload: StockUpdatePayload) => {
    const { new: newData, old: oldData } = payload;
    const { product_id, current_stock } = newData;
    
    // 상품 정보 조회
    const productInfo = options.productInfoMap?.get(product_id);
    const productName = productInfo?.name || '상품';
    const isAvailable = current_stock > 0 && (productInfo?.is_available ?? true);

    onStockUpdate?.(product_id, current_stock, isAvailable);

    const wasAvailable = oldData.current_stock > 0;
    const isNowAvailable = current_stock > 0;

    if (wasAvailable && !isNowAvailable) {
      onOutOfStock?.(product_id, productName);
      debouncedToast(`${productName}이(가) 품절되었습니다`);
    } else if (!wasAvailable && isNowAvailable) {
      onBackInStock?.(product_id, productName);
      debouncedToast(`${productName}이(가) 재입고되었습니다`);
    }
  }, [onStockUpdate, onOutOfStock, onBackInStock, debouncedToast]);

  useEffect(() => {
    if (!restaurantId) return;

    unsubscribeRef.current = realtimeManager.subscribeToRestaurantStock(
      restaurantId,
      handleStockUpdate,
      subscriberIdRef.current
    );

    return () => {
      unsubscribeRef.current?.();
      debouncedToast.cancel();
    };
  }, [restaurantId, handleStockUpdate]);

  return {
    subscriberId: subscriberIdRef.current
  };
}